import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeContextProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { ToastProvider } from './context/ToastContext';
import VersionUpdateBanner from './components/VersionUpdateBanner';
import 'dayjs/locale/sk';
import 'dayjs/locale/en';
import 'dayjs/locale/de';
import 'dayjs/locale/pl';
import 'dayjs/locale/cs';
import 'dayjs/locale/ro';
import AppLayout from './components/navigation/AppLayout';
import ScrollToTop from './components/common/ScrollToTop';
import AdminRoute from './components/admin/AdminRoute';
import { Spinner } from './components/ui';

// Route-level code splitting — each page loads only when navigated to
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const RegisterSuccessPage = React.lazy(() => import('./pages/RegisterSuccessPage'));
const RegisterVerifyPage = React.lazy(() => import('./pages/RegisterVerifyPage'));
const PasswordSetupPage = React.lazy(() => import('./pages/PasswordSetupPage'));
const DashboardPage = React.lazy(() => import('./pages/dashboard/DashboardPage'));
const TenderPage = React.lazy(() => import('./pages/tender/TenderPage'));
const TenderDetailPage = React.lazy(() => import('./pages/tender/TenderDetailPage'));
const CompaniesPage = React.lazy(() => import('./pages/companies/CompaniesPage'));
const CompanyDetailPage = React.lazy(() => import('./pages/companies/CompanyDetailPage'));
const SettingsPage = React.lazy(() => import('./pages/settings/SettingsPage'));
const ExchangePage = React.lazy(() => import('./pages/exchange/ExchangePage'));
const TeamPage = React.lazy(() => import('./pages/team/TeamPage'));
const FreightsPage = React.lazy(() => import('./features/freights/components/FreightsPage'));
const FleetPage = React.lazy(() => import('./pages/fleet/FleetPage'));
const MyBidsPage = React.lazy(() => import('./pages/bids/MyBidsPage'));
const DriversPage = React.lazy(() => import('./pages/drivers/DriversPage'));
const VehiclesPage = React.lazy(() => import('./pages/vehicles/VehiclesPage'));
const FleetMapPage = React.lazy(() => import('./pages/fleet-map/FleetMapPage'));

// Public live-tracking page (no auth) — shared with the Driver app share links
const TrackPage = React.lazy(() => import('./pages/track/TrackPage'));

// Admin panel — separate lazy chunk, never loaded for regular users
const AdminLogin = React.lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminCompanies = React.lazy(() => import('./pages/admin/AdminCompanies'));
const AdminCompanyDetail = React.lazy(() => import('./pages/admin/AdminCompanyDetail'));
const AdminTeams = React.lazy(() => import('./pages/admin/AdminTeams'));
const AdminUsers = React.lazy(() => import('./pages/admin/AdminUsers'));
const AdminProjectUsers = React.lazy(() => import('./pages/admin/AdminProjectUsers'));
const AdminProjectUserDetail = React.lazy(() => import('./pages/admin/project-users/AdminUserDetail'));
const AdminOffers = React.lazy(() => import('./pages/admin/AdminOffers'));
const AdminBids = React.lazy(() => import('./pages/admin/AdminBids'));
const AdminNotifications = React.lazy(() => import('./pages/admin/AdminNotifications'));
const AdminLogs = React.lazy(() => import('./pages/admin/AdminLogs'));
const AdminVehicles = React.lazy(() => import('./pages/admin/AdminVehicles'));
const AdminDrivers = React.lazy(() => import('./pages/admin/AdminDrivers'));
const AdminExternalCarriers = React.lazy(() => import('./pages/admin/AdminExternalCarriers'));
const AdminFleetActivity = React.lazy(() => import('./pages/admin/AdminFleetActivity'));
const AdminReports = React.lazy(() => import('./pages/admin/AdminReports'));

const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center">
    <Spinner size={40} />
  </div>
);

function App() {
  return (
    <ThemeContextProvider>
      <AuthProvider>
        <NotificationProvider>
          <ToastProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Auth routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/register-success" element={<RegisterSuccessPage />} />
                <Route path="/verify-email/:id" element={<RegisterVerifyPage />} />
                <Route path="/setup-password/:token" element={<PasswordSetupPage />} />

                {/* Public live tracking — no auth, no AppLayout (customer-facing) */}
                <Route path="/track/:token" element={<TrackPage />} />

                {/* Protected routes */}
                <Route path="/" element={<AppLayout />}>
                  <Route index element={<DashboardPage />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="tender" element={<TenderPage />} />
                  <Route path="tender/:id" element={<TenderDetailPage />} />
                  <Route path="companies" element={<CompaniesPage />} />
                  <Route path="companies/:id" element={<CompanyDetailPage />} />
                  <Route path="team" element={<TeamPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="exchange" element={<ExchangePage />} />
                  <Route path="freights" element={<FreightsPage />} />
                  <Route path="fleet" element={<FleetPage />} />
                  <Route path="drivers" element={<DriversPage />} />
                  <Route path="vehicles" element={<VehiclesPage />} />
                  <Route path="fleet-map" element={<FleetMapPage />} />
                  <Route path="bids" element={<MyBidsPage />} />
                </Route>

                {/* Admin routes — /admin is the login page, all others require auth */}
                <Route path="/admin" element={<AdminLogin />} />
                <Route element={<AdminRoute />}>
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  <Route path="/admin/companies" element={<AdminCompanies />} />
                  <Route path="/admin/companies/:id" element={<AdminCompanyDetail />} />
                  <Route path="/admin/teams" element={<AdminTeams />} />
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="/admin/project-users" element={<AdminProjectUsers />} />
                  <Route path="/admin/project-users/:id" element={<AdminProjectUserDetail />} />
                  <Route path="/admin/offers" element={<AdminOffers />} />
                  <Route path="/admin/bids" element={<AdminBids />} />
                  <Route path="/admin/notifications" element={<AdminNotifications />} />
                  <Route path="/admin/logs" element={<AdminLogs />} />
                  <Route path="/admin/vehicles" element={<AdminVehicles />} />
                  <Route path="/admin/drivers" element={<AdminDrivers />} />
                  <Route path="/admin/carriers" element={<AdminExternalCarriers />} />
                  <Route path="/admin/fleet-activity" element={<AdminFleetActivity />} />
                  <Route path="/admin/reports" element={<AdminReports />} />
                </Route>

                {/* Fallback */}
                <Route path="*" element={<LoginPage />} />
              </Routes>
            </Suspense>
          </Router>
          <VersionUpdateBanner />
          </ToastProvider>
        </NotificationProvider>
      </AuthProvider>
    </ThemeContextProvider>
  );
}

export default App;
